import { Loading, useAuth, useUser } from '@cibo/profile'
import { usePrograms } from '@cibo/programs'
import { PolyBackgroundWithTheme, THEME_DARK, THEME_LIGHT } from '@cibo/ui'
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material'
import { LayoutSection } from 'components/ImpactLayout'
import { LAND_MANAGER_ROUTES, PROGRAM_ROUTES } from 'navigation/types/routes'
import { ProgramOpportunity } from 'pages/ProgramOpportunity'
import { partition } from 'ramda'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath } from 'react-router-dom'
import { GrowerStepper } from '../GrowerStepper'
import { NeedsInformation } from '../NeedsInformation'
import { useOnboardingDisplayState } from '../useOnboardingDisplayState'

// eslint-disable-next-line complexity
export const OnboardingLayout: FC = ({ children }) => {
  const { t } = useTranslation('OnboardingLayout')
  const {
    palette: { mode },
  } = useTheme()
  const user = useUser()
  const { persona = 'grower' } = useAuth()
  const {
    firstVisit,
    isLoading,
    enrollments,
    fieldCounts: { eligible, unprofiled, total, enrolled, sensing },
    isOnboarding,
  } = useOnboardingDisplayState()
  const programs = usePrograms()

  if (isLoading) {
    return <Loading />
  }

  const [programOpportunities] = partition(
    p => !!p.program.opportunitiesByWfStatus?.notInWorkflow.some(o => !!o.numResources),
    programs.data || []
  )
  const activeStep =
    total === 0 && sensing === 0 ? 0 : !!enrolled ? 3 : !!enrollments?.length ? 2 : 1

  const addFieldsButton = (
    <Button component={Link} to={LAND_MANAGER_ROUTES.ADD} color="secondary" variant="contained">
      {t('addFieldsButton')}
    </Button>
  )

  const themeReset = mode === 'light' ? THEME_LIGHT : THEME_DARK

  const sensingMessage = sensing > 0 && (
    <Stack direction="row" spacing={1} alignItems="center">
      <CircularProgress size={16} />
      <Typography>{t('sensingMessage', { count: sensing })}</Typography>
    </Stack>
  )

  return (
    <>
      {isOnboarding && persona === 'grower' ? (
        <ThemeProvider theme={THEME_DARK}>
          <Collapse in={!!firstVisit || activeStep !== 2}>
            <LayoutSection sx={{ padding: 0 }}>
              <PolyBackgroundWithTheme>
                <LayoutSection container>
                  <Typography variant="h4">
                    {t(firstVisit ? 'welcomeHeadline' : 'hiHeadline', {
                      name: user.data?.displayNameGiven,
                    })}
                  </Typography>
                  {isOnboarding && (
                    <Stack spacing={2}>
                      <GrowerStepper activeStep={activeStep} />

                      {activeStep === 0 && (
                        <Stack sx={{ maxWidth: '450px' }} spacing={1} alignItems="start">
                          <Typography>{t('stepDescription', { context: activeStep })}</Typography>
                          {addFieldsButton}
                        </Stack>
                      )}
                      {activeStep === 1 && (
                        <Stack spacing={1}>
                          <Stack
                            sx={{ maxWidth: '450px' }}
                            spacing={1}
                            alignItems="center"
                            alignSelf="center"
                          >
                            {}
                            {eligible > 0 && programOpportunities.length > 0 ? (
                              <>
                                <Typography>
                                  {t('eligiblePrograms', {
                                    count: programOpportunities.length,
                                    fieldsAre: t('fieldsAre', { count: eligible }),
                                    fields: t('fields', { count: eligible }),
                                  })}
                                </Typography>
                                {sensingMessage}
                              </>
                            ) : !!unprofiled ? (
                              <>
                                <NeedsInformation />
                                {sensingMessage}
                              </>
                            ) : sensing > 0 ? (
                              <Stack
                                sx={{ maxWidth: '450px' }}
                                spacing={1}
                                alignItems="center"
                                alignSelf="center"
                              >
                                <Typography variant="h5">
                                  {t('sensingTitle', { count: sensing })}
                                </Typography>
                                <Typography>{t('sensingBody', { count: sensing })}</Typography>
                              </Stack>
                            ) : (
                              <Stack
                                sx={{ maxWidth: '450px' }}
                                spacing={1}
                                alignItems="center"
                                alignSelf="center"
                              >
                                <Typography variant="h5">
                                  {t('ineligible', { count: total })}
                                </Typography>
                                <Typography>{t('ineligibleBody', { count: total })}</Typography>
                                {addFieldsButton}
                              </Stack>
                            )}
                          </Stack>
                          <Grid
                            spacing={3}
                            justifyContent="center"
                            container
                            width="100%"
                            alignSelf="stretch"
                          >
                            <ThemeProvider theme={themeReset}>
                              {programOpportunities.slice(0, 3).map(({ programId }) => (
                                <Grid key={programId} xs={12} md={6} lg={4} item container>
                                  <ProgramOpportunity
                                    sx={{ flex: 1, height: '100%' }}
                                    programId={programId}
                                    LinkComponent={Link}
                                    to={generatePath(PROGRAM_ROUTES.ENROLL, { programId })}
                                  />
                                </Grid>
                              ))}
                            </ThemeProvider>
                            {programOpportunities.length > 3 && (
                              <Grid xs={12} md={6} lg={4} item>
                                <Button
                                  fullWidth
                                  component={Link}
                                  to={PROGRAM_ROUTES.ROOT}
                                  variant="outlined"
                                  endIcon={<ChevronRightRounded />}
                                >
                                  {t('seeAllOpportunities')}
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Stack>
                      )}

                      {activeStep === 2 && (
                        <Stack
                          spacing={1}
                          alignSelf="flex-end"
                          pr={1}
                          textAlign="right"
                          maxWidth={450}
                        >
                          {!!enrollments?.length && (
                            <>
                              <Typography>
                                {t('stepDescription', { context: activeStep })}
                              </Typography>
                              <Box>
                                <Button
                                  component={Link}
                                  to={generatePath(PROGRAM_ROUTES.ENROLL_DETAIL, {
                                    programId: enrollments[0].programId,
                                    workflowId: enrollments[0].id,
                                  })}
                                  variant="contained"
                                  color="warning"
                                  endIcon={<ChevronRightRounded />}
                                >
                                  {t('finishEnrollment')}
                                </Button>
                              </Box>
                            </>
                          )}
                        </Stack>
                      )}

                      {activeStep === 3 && (
                        <Stack
                          spacing={1}
                          alignSelf="flex-end"
                          pr={1}
                          textAlign="right"
                          maxWidth={450}
                        >
                          {!!enrolled && !!programOpportunities.length ? (
                            <>
                              <Typography>
                                {t('stepDescription', { context: 'completeEnrollMore' })}
                              </Typography>
                              <Box>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  component={Link}
                                  to={PROGRAM_ROUTES.ROOT}
                                  endIcon={<ChevronRightRounded />}
                                >
                                  {t('moreOpportunities')}
                                </Button>
                              </Box>
                            </>
                          ) : !!enrolled ? (
                            <>
                              <Typography>
                                {t('stepDescription', { context: 'completeAddFields' })}
                              </Typography>
                              <Box>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  component={Link}
                                  to={LAND_MANAGER_ROUTES.ADD}
                                  endIcon={<ChevronRightRounded />}
                                >
                                  {t('addFieldsButton')}
                                </Button>
                              </Box>
                            </>
                          ) : null}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </LayoutSection>
              </PolyBackgroundWithTheme>
            </LayoutSection>
          </Collapse>
        </ThemeProvider>
      ) : null}
      {children}
    </>
  )
}
